import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape FIN-04 publishes, imported rather than restated.
import type { Refund } from "@tdelivr/api-client/generated/finance/refund.js";

import { exportSheet, type ExportFormat } from "../export.ts";
import {
  againstCell,
  amountCell,
  fetchedNote,
  goodwillDetail,
  goodwillPage,
  PER_FETCH,
  stateOf,
  statesPresent,
  type GoodwillOrder,
} from "../goodwill.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { GridPager, GridToolbar, NoMatch } from "./DataGrid.tsx";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Refunds raised from cases, and what each is waiting on.
 *
 * Built to `20 WP19 Support Console.dc.html`, page `refunds`, on that file's one `gridUI()`.
 *
 * **ADR 0027 published the listing this screen needed.** `docs/adr/0027` records it: "`support ·
 * Goodwill and refunds` stays blocked. `GET /v1/refunds` is one of its four essential absences" —
 * that one is closed, and of the other three two are the same absence stated twice.
 *
 * **It reads the same operation the finance console's refunds board reads, and asks a different
 * question of it.** Finance asks "is this refund right"; support asks "what did we give this
 * customer, and is it waiting on somebody". So three of the six state words differ — `raised`,
 * `issued`, `rejected` where finance says `requested`, `completed`, `failed` — because they are two
 * approved pages describing the same rows to two different readers, and CLAUDE.md section 1 says
 * keep each page's copy word for word. The *classes* are identical, so the two boards can never
 * disagree about what a state means.
 *
 * **The `Case` column is the middle of three and has no source.** The design writes `cs_4455 · fare
 * dispute`. Nothing on FIN-04 or any contract links a refund to a case — no `caseId`, no
 * `sourceReference` — and SUP-04, which holds case links, is entirely `/internal/v1`. The cell
 * draws the refund's own `reasonCode`, the panel draws the case as an explicit absence with the
 * reason on it, and nothing composes a `cs_` string. R-35.
 *
 * **The page's governing rule has no source.** Its description is "Over the agent limit goes to a
 * supervisor", its tile reads `Awaiting supervisor · 2 · over KES 2,000`, and its closing note is
 * "An agent cannot approve a refund they raised." FIN-04's status enum has no approval state and
 * nothing publishes who raised a refund, so no row is drawn as awaiting a supervisor. The note is
 * kept word for word, because it is the product's rule whether or not the service enforces it, and
 * this is the gap on this screen most worth closing.
 *
 * **Credits are not drawn, because they do not exist.** The design's rows include `cr_1120 · KES
 * 200.00 credit · issued`. Nothing on the platform models a goodwill credit: PAY-02's wallet
 * operations are internal and a top-up is not a credit against a case.
 *
 * **No tiles, where the design draws four.** `Raised today · 17` and `Credits issued · KES 8,420.00`
 * are sums over a window FIN-04 takes no parameter for; `Awaiting supervisor · 2` has no source at
 * all; `Rejected · 1` would be a count of what was fetched. R-04 measured that no listing on this
 * platform publishes a total — and on a board of money a count that looks like one ends up in a
 * reconciliation.
 */

const refundApi = services.finance__refund;

export interface GoodwillRefundsProps {
  readonly transport: Transport;
  /** Innermost last: tenant, console, group, page. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly refunds: readonly Refund[];
  /** Absent on the last page rather than empty — ADR 0027 §1. */
  readonly cursor: string | undefined;
}

export function GoodwillRefunds({ transport, crumbs }: GoodwillRefundsProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedRefundId, setSelectedRefundId] = useState<string | undefined>(undefined);
  const [state, setState] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<GoodwillOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);

  const load = useCallback(async () => {
    const client = refundApi.createRefundClient(transport);
    try {
      // Neither filter sent. ADR 0027 §4: every filter is optional and the default is to filter
      // nothing, because a listing that silently hides rows is worse than one that shows too many.
      const answered = await client.listRefunds(undefined, undefined, String(PER_FETCH), undefined);
      setLoaded({ refunds: answered.refunds, cursor: answered.nextCursor });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["refund:read"]));
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  const loadOlder = useCallback(async () => {
    const held = loaded;
    if (held?.cursor === undefined || fetching) return;
    setFetching(true);
    const client = refundApi.createRefundClient(transport);
    try {
      const answered = await client.listRefunds(
        undefined,
        undefined,
        String(PER_FETCH),
        held.cursor,
      );
      setLoaded({
        refunds: [...held.refunds, ...answered.refunds],
        cursor: answered.nextCursor,
      });
      setPage((current) => current + 1);
    } catch (error) {
      setFailure(stateForError(error, ["refund:read"]));
    } finally {
      setFetching(false);
    }
  }, [loaded, fetching, transport]);

  const rows = loaded?.refunds ?? [];
  /* Across everything fetched, so the panel survives a page turn or a narrowed search. */
  const selectedRefund = loaded?.refunds.find((row) => row.refundId === selectedRefundId);

  const view = useMemo(
    () => goodwillPage(rows, { search, order, page, perPage }, state),
    [rows, search, order, page, perPage, state],
  );

  const viewState = failure ?? stateForList(loaded === undefined ? undefined : rows);

  const clearFilters = (): void => {
    setSearch("");
    setState(undefined);
    setPage(1);
  };

  const onNext = (): void => {
    if (page < view.pageCount) setPage(page + 1);
    else void loadOlder();
  };

  const note = fetchedNote(loaded?.refunds.length, loaded?.cursor !== undefined);

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Request</span>
      <span>Case</span>
      <span>Amount</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      {/*
        The page and its detail panel in one positioned container, which is what docks the panel.

        Both docking rules are written against `.board-split`: at 1240 and above
        `.board-split:has(> .detail)` makes the panel a 420px second column, and below 1240 it is
        `position: absolute; inset: 0 0 0 auto`, positioned against `.board-split`'s own
        `position: relative`. This board had neither — the panel was rendered inside the page, and
        on seven of these twelve boards inside the closing-note callout, where it was a flex item
        beside an `info` glyph and a sentence.

        `Settlement.tsx` and `Invoices.tsx` were already this shape, which is why they are the
        shape copied here rather than a new one. Found by the census in
        `tooling/scripts/lib/detail-panels.mjs`: this panel had never been photographed, so
        nothing compared it against the design it is built from. Which docking is right at all is
        Q-297.
      */}
      <section className="page records goodwill">
        <PageHeading
          crumbs={crumbs ?? []}
          title="Goodwill and refunds"
          source="FIN-04"
          description="Refunds and credits raised from cases. Over the agent limit goes to a supervisor."
          action={
            <button
              type="button"
              className="records__primary"
              /*
               * Drawn and refused. The route exists and the console cannot fill its body — which is
               * the same refusal the finance console's Issue refund carries, for the same reason and
               * with one more problem on top of it here.
               *
               * `POST /v1/refunds` requires `refundId`, `paymentIntentId`, `amountMinor`,
               * `capturedAmountMinor`, `previouslySucceededMinor`, `currency` and `reasonCode`. The
               * design's form collects four: `Case`, `Type`, `Amount (KES)` and `Reason`. So
               * `paymentIntentId`, `capturedAmountMinor`, `previouslySucceededMinor` and `currency`
               * have no field on it — all four are properties of the *payment*, which this console
               * would have to read from a second service to know.
               *
               * And the extra problem: the form's first field is `Case`, and **nothing links a case
               * to a payment**. So even with the payment service on this screen there would be no
               * way to get from the case an agent is looking at to the payment a refund is against.
               * That is a service to build, not a payload to compose.
               */
              disabled
              title="POST /v1/refunds requires paymentIntentId, capturedAmountMinor, previouslySucceededMinor and currency, none of which the design's form collects and all of which are properties of the payment. And nothing links a case to a payment, so the form's Case field cannot reach one. See R-35."
            >
              <span aria-hidden="true" className="records__primary-glyph">
                add
              </span>
              Raise refund
            </button>
          }
        />

        <div className="records__panel">
          <GridToolbar
            idPrefix="goodwill"
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setPage(1);
            }}
            state={state}
            onState={(next) => {
              setState(next);
              setPage(1);
            }}
            states={statesPresent(rows)}
            order={order}
            onOrder={setOrder}
            onExport={(format: ExportFormat) => {
              exportSheet(sheetFor(view.all), format, "goodwill-and-refunds");
            }}
          />

          <StateView
            state={viewState}
            purpose="Refunds appear here as soon as one is raised against a payment."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "records__row",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            {head}
            <div className="records__rows">
              {view.rows.map((refund) => {
                const shown = stateOf(refund);
                return (
                  <div
                    key={refund.refundId}
                    data-selected={refund.refundId === selectedRefundId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={refund.refundId === selectedRefundId}
                    aria-label={`Open refund ${refund.refundId}`}
                    onClick={() => setSelectedRefundId(refund.refundId)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedRefundId(refund.refundId);
                    }}
                    className={
                      shown.classKey === "failed"
                        ? "records__row goodwill__row--rejected"
                        : "records__row"
                    }
                  >
                    <span className="records__lead" title={refund.refundId}>
                      {refund.refundId}
                    </span>
                    <span
                      className="records__cell"
                      /*
                       * The reason, in the cell the design gives to a case. The title says which, so
                       * a reader is never left to infer that a reason code is a case identifier.
                       */
                      title={[
                        `reason code ${refund.reasonCode}`,
                        `payment intent ${refund.paymentIntentId}`,
                        "The design draws the case this refund was raised from. Nothing links a refund to a case — see R-35.",
                      ].join("\n")}
                    >
                      {againstCell(refund)}
                    </span>
                    <span
                      className="records__cell"
                      title={[
                        `raised ${refund.requestedAt}`,
                        `captured on the payment ${String(refund.capturedAmountMinor)} ${refund.currency} minor units`,
                        `already refunded ${String(refund.previouslySucceededMinor)} ${refund.currency} minor units`,
                      ].join("\n")}
                    >
                      {amountCell(refund)}
                    </span>
                    <span className="records__state">
                      <StateBadge
                        code="FIN-04"
                        status={refund.status}
                        classKey={shown.classKey}
                        label={shown.label}
                        title={`FIN-04 publishes ${refund.status}. ${describeStatus(refund)}`}
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            {view.total === 0 ? <NoMatch onClear={clearFilters} /> : null}
            <GridPager
              idPrefix="goodwill"
              page={page}
              pageCount={view.pageCount}
              perPage={perPage}
              onPerPage={(next) => {
                setPerPage(next);
                setPage(1);
              }}
              total={view.total}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={onNext}
              more={loaded?.cursor !== undefined}
              fetching={fetching}
            />
          </StateView>

          {note === undefined ? null : (
            <p className="records__bounded" role="status">
              {note}
            </p>
          )}
        </div>

        {/* The design's closing note, word for word, in its own `lock` kind. */}
        <div className="records__note records__note--warn">
          <span aria-hidden="true" className="records__note-glyph">
            lock
          </span>
          <span>An agent cannot approve a refund they raised.</span>
        </div>
      </section>

      {/* The selected row, in full. */}
      {selectedRefund === undefined ? null : (
        <RecordDetail
          record={goodwillDetail(selectedRefund)}
          open
          onClose={() => setSelectedRefundId(undefined)}
        />
      )}
    </div>
  );
}

/**
 * What a status means, for the badge's title.
 *
 * The two that matter are `FAILED` and `OUTCOME_UNKNOWN`, and on this console they matter for a
 * reason finance's version does not have: an agent says these words to a customer. `rejected` reads
 * as "we said no" and means "the provider refused it"; `outcome unknown` must not be reported as
 * either answer.
 */
function describeStatus(refund: Refund): string {
  switch (refund.status) {
    case "REQUESTED":
      return "It is recorded and has not been sent to the provider yet. Nothing on the platform asks a supervisor to review it first, which the design's rule says should happen.";
    case "PROCESSING":
      return "It has been sent to the provider and no outcome has come back.";
    case "SUCCEEDED":
      return `The provider confirmed it${refund.completedAt === undefined ? "" : ` at ${refund.completedAt}`}. The customer has it.`;
    case "FAILED":
      return "The provider refused it — not a supervisor, and not this company. FIN-04 publishes no reason for the refusal, so do not tell the customer why.";
    case "CANCELLED":
      return "It was cancelled before it was sent.";
    case "OUTCOME_UNKNOWN":
      return "The provider call did not answer, so neither success nor failure is true yet. Do not tell the customer either one, and do not raise a second refund — FIN-04 resolves it on its own.";
  }
}

/**
 * The rectangle an export writes: the columns on screen, in the words on screen.
 *
 * Three columns more than the board. `Payment` is what the refund is actually against, which the
 * `Case` column could not be; `Raised` is the field the listing is ordered by, and a row read out
 * of a file months later has no "most recent first" to be read against; `Still refundable` is the
 * figure a reader checks the amount against, and a spreadsheet is read without hover.
 */
function sheetFor(rows: readonly Refund[]): {
  header: readonly string[];
  rows: readonly (readonly string[])[];
} {
  return {
    header: ["Request", "Reason", "Amount", "State", "Payment", "Still refundable", "Raised"],
    rows: rows.map((refund) => [
      refund.refundId,
      refund.reasonCode,
      amountCell(refund),
      stateOf(refund).label,
      refund.paymentIntentId,
      amountCell({
        ...refund,
        amountMinor: refund.capturedAmountMinor - refund.previouslySucceededMinor,
      }),
      refund.requestedAt,
    ]),
  };
}
